import React, { useState, useRef, useEffect } from "react";
import {
  FormControl,
  MenuItem,
  Menu,
  Button,
  Typography,
  ListItemText,
  Box,
  Chip,
  ListSubheader
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import muiCustomStyles from "../styles";
import { useTranslation } from "react-i18next";
import CustomCheckboxLabel from "../../CustomCheckboxLabel";

const MultiSelectDropdown = ({
  value = [],
  onChange,
  options,
  placeholder = "Select",
  buttonText = "",
  width = "100%",
  buttonHeight = 39,
  disabled = false,
  buttonIconPath = "",
  showChips = false,
  sx = {},
  grouped = false,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [buttonWidth, setButtonWidth] = useState(null);
  const buttonRef = useRef(null);
  const open = Boolean(anchorEl);

  const { t } = useTranslation("components");

  useEffect(() => {
    const currentButton = buttonRef.current;

    const updateButtonWidth = () => {
      if (currentButton) {
        setButtonWidth(currentButton.offsetWidth);
      }
    };

    updateButtonWidth();

    const resizeObserver = new ResizeObserver(updateButtonWidth);
    if (currentButton) {
      resizeObserver.observe(currentButton);
    }

    return () => {
      if (currentButton) {
        resizeObserver.unobserve(currentButton);
      }
    };
  }, []);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleToggle = (optionValue) => {
    const newValue = value.includes(optionValue)
      ? value.filter((v) => v !== optionValue)
      : [...value, optionValue];
    onChange(newValue);
  };

  const handleDeleteChip = (itemValue) => {
    const updated = value.filter((val) => val !== itemValue);
    onChange(updated);
  };

  const selectedOptions = grouped
    ? options
      .flatMap((group) => group.options)
      .filter((item) => value.includes(item.value))
    : options.filter((item) => value.includes(item.value));

  const getButtonLabel = () => {
    if (selectedOptions.length === 0) return placeholder;
    if (selectedOptions.length === 1) return selectedOptions[0].label;
    return `${selectedOptions.length} ${buttonText} ${t(
      "combobox_multi_select"
    )}`;
  };

  const {
    chipStyles,
    formControl,
    buttonBase,
    buttonOpen,
    text,
    menuPaper,
    menuItem,
    menuItemText,
    chipContainer,
    iconLabelWrapper,
    listSubHeader,
  } = muiCustomStyles;

  return (
    <FormControl sx={{ ...formControl, minWidth: width, ...sx.formControl }}>
      <Button
        ref={buttonRef}
        disabled={disabled}
        onClick={handleClick}
        variant="outlined"
        size="small"
        sx={{
          ...buttonBase,
          ...(open && buttonOpen),
          height: buttonHeight,
          ...sx.button,
        }}
      >
        <Box sx={iconLabelWrapper}>
          {buttonIconPath && (
            <img src={buttonIconPath} alt="icon" width={18} height={18} />
          )}

          {showChips && selectedOptions.length > 0 ? (
            <Box sx={chipContainer}>
              {selectedOptions.map((opt) => (
                <Chip
                  key={opt.value}
                  label={opt.label}
                  onDelete={() => handleDeleteChip(opt.value)}
                  sx={{ ...chipStyles, ...sx.chip }}
                  deleteIcon={<CloseIcon />}
                />
              ))}
            </Box>
          ) : (
            <Typography sx={{ ...text, ...sx.text }}>
              {getButtonLabel()}
            </Typography>
          )}
        </Box>
        <img src="/icons/arrow-down.svg" alt="arrow-down" />
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        disableAutoFocusItem
        MenuListProps={{
          "aria-labelledby": "dropdown-button",
          disablePadding: true,
        }}
        PaperProps={{
          style: {
            ...menuPaper,
            minWidth: buttonWidth || "auto",
            width: buttonWidth || "auto",
            ...sx.menuPaper,
          },
        }}
        sx={{
          ...sx.menu,
          "& .MuiPaper-root": {
            paddingTop: grouped ? "0px !important" : "8px",
          },
        }}
      >
        {grouped
          ? options.map((group, groupIdx) => (
            <Box key={groupIdx}>
              <ListSubheader sx={listSubHeader}>
                {group.groupLabel}
              </ListSubheader>
              {group.options.map((option, idx) => (
                <MenuItem
                  key={option.value}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggle(option.value);
                  }}
                  disableRipple
                  sx={{
                    ...menuItem,
                    mb:
                      groupIdx === options.length - 1 &&
                        idx === group.options.length - 1
                        ? 0
                        : "6px",
                    ...sx.menuItem,
                  }}
                >
                  <CustomCheckboxLabel checked={value.includes(option.value)} />
                  <ListItemText
                    primary={
                      <Typography
                        sx={{ ...menuItemText, ...sx.menuItemText }}
                      >
                        {option.label}
                      </Typography>
                    }
                  />
                </MenuItem>
              ))}
            </Box>
          ))
          : options.map((option, idx) => (
            <MenuItem
              key={option.value}
              onClick={(e) => {
                e.stopPropagation();
                handleToggle(option.value);
              }}
              disableRipple
              sx={{
                ...menuItem,
                mb: idx !== options.length - 1 ? "12px" : 0,
                ...sx.menuItem,
              }}
            >
              <CustomCheckboxLabel checked={value.includes(option.value)} />
              <ListItemText
                primary={
                  <Typography sx={{ ...menuItemText, ...sx.menuItemText }}>
                    {option.label}
                  </Typography>
                }
              />
            </MenuItem>
          ))}
      </Menu>
    </FormControl>
  );
};

export default MultiSelectDropdown;
